import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';

import BackflowLogFormCommentOnly from './BackflowLogFormCommentOnly';
import BackflowLogFormFull from './BackflowLogFormFull';
import BackflowLogSubmitButtons from './BackflowLogSubmitButtons';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import OutOfServiceCheckbox from './OutOfServiceCheckbox';

import { BackflowLog, BackflowJobItem } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowLogSectionProps {
  backflowJobItem: BackflowJobItem;
  jobStarted: boolean;
  fetchBackflowJobItem: () => void;
}

const outOfServiceStatuses = ['out_of_service', 'hidden'];

const BackflowLogSection: React.FC<BackflowLogSectionProps> = ({
  backflowJobItem,
  jobStarted,
  fetchBackflowJobItem,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<BackflowLog>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [outOfService, setOutOfService] = useState<boolean>(false);
  const [requiredImagesAttached, setRequiredImagesAttached] = useState(false);
  const [showRequiredImageErrors, setShowRequiredImageErrors] = useState(false);

  const fetchBackflowLog = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>(`/backflows/backflow_logs/${backflowJobItem.id}`);
      setFormValues(response.data.result || {});
    } catch (err) {
      setErrorMessage('Something went wrong fetching the Backflow Log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!backflowJobItem?.id) return;
    fetchBackflowLog();
  }, [backflowJobItem?.id]);

  useEffect(() => {
    const updatedServiceStatus = outOfServiceStatuses.includes(backflowJobItem?.status);
    setOutOfService(updatedServiceStatus);
  }, [backflowJobItem?.status]);

  useEffect(() => {
    if (requiredImagesAttached) setShowRequiredImageErrors(false);
  }, [requiredImagesAttached]);

  const showFullForm = jobStarted && !outOfService;
  const showSubmitButtons = jobStarted || outOfService;

  return (
    <Grid container>
      <LoadingWrapper isLoading={loading}>
        <Grid container>
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>

          <Grid item xs={12}>
            <OutOfServiceCheckbox outOfService={outOfService} setOutOfService={setOutOfService} />
          </Grid>

          {outOfService && (
            <Grid item xs={12}>
              <BackflowLogFormCommentOnly formValues={formValues} setFormValues={setFormValues} />
            </Grid>
          )}

          {showFullForm && (
            <Grid item xs={12}>
              <BackflowLogFormFull
                backflowJobItem={backflowJobItem}
                formValues={formValues}
                setFormValues={setFormValues}
                onRequiredImagesValidityChange={setRequiredImagesAttached}
                showRequiredImageErrors={showRequiredImageErrors}
              />
            </Grid>
          )}

          {showSubmitButtons && (
            <Grid item xs={12} style={{ marginTop: 15 }}>
              <BackflowLogSubmitButtons
                backflowJobItem={backflowJobItem}
                formValues={formValues}
                outOfService={outOfService}
                fetchBackflowJobItem={fetchBackflowJobItem}
                requiredImagesAttached={showFullForm ? requiredImagesAttached : true}
                onRequiredImagesValidationFailed={() => setShowRequiredImageErrors(true)}
              />
            </Grid>
          )}
        </Grid>
      </LoadingWrapper>
    </Grid>
  );
};

export default BackflowLogSection;
